import React from 'react';
import type { Filter } from '../../@types/ElasticSearchResponse';
import type { CombinedFacet } from '../../utils/reselectors';

const getPercentage = (value: number, total: number) => {
  return (100 / total) * value;
};

const getHistogramRatio = (largest: number) => {
  const isTooLarge = largest > 100;

  if (isTooLarge) {
    return 1;
  }
  return Math.floor(100 / largest);
};

const getHistogramBars = (filters: Filter[]) => {
  const totalCount = filters.reduce((accumulator, f) => accumulator + f.count, 0);
  const bars: { [key: string]: number | undefined } = {};

  let largest = 0;

  filters.forEach((filter) => {
    const percentage = getPercentage(filter.count, totalCount);
    const isNewLargest = percentage > largest;

    bars[filter.query] = percentage;

    if (isNewLargest) {
      largest = percentage;
    }
  });

  const ratio = getHistogramRatio(largest);

  return {
    ratio,
    bars,
  };
};

interface SelectFilterRangeHistogramProps {
  facet: CombinedFacet;
}

const SelectFilterRangeHistogram = ({ facet }: SelectFilterRangeHistogramProps) => {
  const { histogram, filters } = facet;

  const shouldShowHistogram = histogram && filters && filters.length > 0;

  if (!shouldShowHistogram) {
    return null;
  }

  const { bars, ratio } = getHistogramBars(filters);

  const barItems = filters.map((filter) => {
    const displayName = filter.displayName
      .replace(facet.prefixChar || '', '')
      .replace(facet.suffixChar || '', '');

    const value = Number.parseFloat(displayName);
    const isActive = value >= (facet.range?.min || 0) && value <= (facet.range?.max || 0);

    const count = bars[filter.query] as number;
    const height = ratio * count;

    return (
      <div key={filter.displayName} className="histogram-column">
        <div
          className={`column-fill${isActive ? ' is-active' : ''}`}
          style={{ height: `${height}%` }}
        />
      </div>
    );
  });

  return (
    <div className="histogram-container">
      <div className="histogram-chart">{barItems}</div>
    </div>
  );
};

export default SelectFilterRangeHistogram;
